import { message } from "ant-design-vue";
import apiInstance from "@/axios";
import { errorCode, errorMessage, isStepUpCancelled } from "@/utils/mfa";

/**
 * 「국외 허용 IP」 API (inapi, SPEC-SENDCERT-001 D-138·D-139). 운영자(CERTILIFE)만 쓴다.
 * 쓰기는 운영자 본인 확인 증표가 늘 필요하다 — 증표가 없으면 403 MFA_REQUIRED 가 오고
 * axios 인터셉터가 본인 확인 창을 띄운 뒤 다시 보낸다(AccountSecurityModal 의 국외 예외 등록과 같은 방식).
 */
const BASE = "/admin/security/geo-allow-ips";
const quiet = { showGlobalLoading: false };
const rows = (data) => (Array.isArray(data) ? data : (data && data.items) || []);

// 회사 고르기 종류. 모두 이 앱이 이미 쓰는 업체 목록 API(POST /companies/list/v2)로 찾는다(새 서버 API 없음).
// ponytail: STUDIO 도 같은 API(type=STUDIO) — 회사 type 이 계정 Role 이라 그대로 걸린다
export const COMPANY_TYPES = [
  { value: "HOSPITAL", label: "병원" },
  { value: "MANUFACTURER", label: "제조사" },
  { value: "DENTAL_LABORATORY", label: "기공소" },
  { value: "STUDIO", label: "STUDIO" },
  { value: "RESELLER", label: "리셀러" },
];

/**
 * 실패 알림(서버 400 문구는 그대로). 본인 확인 창을 스스로 닫은 것이면 알리지 않는다(D5).
 * GEO_BLOCKED 는 axios 인터셉터가 같은 key 로 이미 알렸으므로 같은 key 로 덮어 한 번만 보인다(AccountSecurityModal 과 같음)
 */
export const notifyFailure = (error, fallback) => {
  console.error(error);
  if (isStepUpCancelled(error)) return;
  const code = errorCode(error);
  message.error({
    content: errorMessage(error, fallback),
    key: code === "GEO_BLOCKED" ? "GEO_BLOCKED" : undefined,
  });
};

/**
 * 「해외 접속 차단」 모드(D-142). 응답 { mode, source:'db'|'env', envMode, updatedBy, updatedAt, reason }.
 * 바꾸기(PUT)는 운영자 본인 확인 증표가 늘 필요하다 — 위 쓰기와 같이 인터셉터가 창을 띄우고 다시 보낸다
 */
const GEO_MODE = "/admin/security/geo-mode";
export const GeoModeApi = {
  get: () => apiInstance.get(GEO_MODE, quiet).then(({ data }) => data),
  set: (mode, reason) => apiInstance.put(GEO_MODE, { mode, reason }).then(({ data }) => data),
  // 최근 50건 [{ action:'GEO_MODE_CHANGED', actor, detail:{ before, after, reason }, createdAt }]
  history: () => apiInstance.get(`${GEO_MODE}/history`, quiet).then(({ data }) => rows(data)),
};

export default {
  list: () => apiInstance.get(BASE, quiet).then(({ data }) => rows(data)),
  history: () => apiInstance.get(`${BASE}/history`, quiet).then(({ data }) => rows(data)),
  create: (body) => apiInstance.post(BASE, body),
  update: (id, body) => apiInstance.put(`${BASE}/${id}`, body),
  remove: (id) => apiInstance.delete(`${BASE}/${id}`),
  // D-139 고객사 신청 승인(cidr·until 을 고쳐 보낼 수 있음)·거절(사유 필수). 쓰기라 본인 확인 증표가 늘 필요
  approve: (id, body) => apiInstance.put(`${BASE}/${id}/approve`, body),
  reject: (id, reason) => apiInstance.put(`${BASE}/${id}/reject`, { reason }),
  /**
   * 승인 대기 건수(메뉴 배지, inapi 응답 { count }). 화면을 옮길 때마다 몰래 부르므로
   * 본인 확인이 필요하다고 와도(inapi 조회 관례 mfa:'mode') 창을 띄우지 않고 배지만 그대로 둔다
   */
  pendingCount: () =>
    apiInstance
      .get(`${BASE}/pending-count`, { ...quiet, showErrorNotice: false, skipStepUp: true })
      .then(({ data }) => Number(data && typeof data === "object" ? data.count : data) || 0),

  /** 회사 이름으로 찾기(지워지지 않은 회사, 최대 20곳) → [{ companyId, name }] */
  searchCompanies: (type, keyword) =>
    apiInstance
      .post(
        "/companies/list/v2",
        { page: 1, limit: 20, type, search: "name", condition: keyword, deleteYn: "N" },
        quiet
      )
      .then(({ data }) =>
        ((data && data.docs) || []).map((c) => ({
          companyId: String(c._id),
          name: c.name,
          // 같은 이름 회사를 구분하는 보조 정보(사업자번호·대표·지역)
          hint: [c.cNo, c.ceo && `대표 ${c.ceo}`, [c.addrOne, c.addrTwo].filter(Boolean).join(" ")]
            .filter(Boolean)
            .join(" · "),
        }))
      ),
};
